/* eslint-disable @typescript-eslint/no-magic-numbers */
import React, { FormEventHandler, HTMLProps, KeyboardEventHandler, useCallback, useEffect } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { Box } from '@cp/ds/src/components/box';
import { Typography } from '@cp/ds/src/components/typography';
import { AttachButton, DialogFooter, SendButton, coreDialogApi, useDialog } from '@cp/entities/dialog';
import { SxProps } from '@cp/ds';
import { handleApiServerError } from '@cp/utils/apiServer/errorHandler';
import { AnnounceHintWrapper } from '@cp/ds/src/components/announceHintWrapper';
import { ButtonProps } from '@cp/ds/src/components/button';
import { useDialogUserService } from 'src/features/dialog/view';
import { FormControlProps, __experimentalFormField as FormField, withChangeableValue, withErrorTooltip } from '@cp/ds/src/components/form';
import { FieldTokens } from '@youhelp-ru/ui';
import { TextAreaAutoSize } from '@cp/ds/src/components/form/components/field/textAreaAutoSize';
import { CheckboxButton } from '@cp/ds/src/components/checkbox';
import { createFromValidationResolver } from '@cp/utils/form/resolver';

import { IsNotEmpty, MaxLength, ValidateIf } from 'class-validator';
import { Transform } from 'class-transformer';
import { generateCreateMessageRequest } from '../../model';

const MESSAGE_MAX_LENGTH_WHEN_SMS_COPY_IS_REQUIRED = 134;
export class MessageFormValues {
  @IsNotEmpty()
  dialogId: number;

  @IsNotEmpty({ message: 'Пустое сообщение' })
  @ValidateIf((o: MessageFormValues) => o.sendCopyBySms)
  @MaxLength(MESSAGE_MAX_LENGTH_WHEN_SMS_COPY_IS_REQUIRED, {
    message: (validationArguments) => `Максимальная рекомендуемая длина смс сообщения ${validationArguments.constraints[0]} символов`,
  })
  @Transform(({ value }) => value.trim())
  message: string;

  sendCopyBySms: boolean;
}
const resolver = createFromValidationResolver(MessageFormValues);
const calcSmsCount = (length: number) => {
  if (length < 71) {
    return 1;
  } else if (length < 135) {
    return 2;
  } else if (length < 202) {
    return 3;
  } else if (length < 269) {
    return 4;
  } else {
    return 5;
  }
};
const MessageControl: React.FC<FormControlProps<string, HTMLTextAreaElement>> = withChangeableValue(
  withErrorTooltip((p) => <TextAreaAutoSize error={p.invalid} maxRows={5} minRows={1} sx={{ width: FieldTokens.BaseWidth() }} {...p} />),
);

const MessageFormField: React.FC<Omit<HTMLProps<HTMLTextAreaElement>, 'size'>> = ({ children, name = 'message', ...props }) => {
  const message: string = useWatch({ name });
  const sendBySms = useWatch<MessageFormValues>({ name: 'sendCopyBySms' });
  return (
    <FormField<string, HTMLTextAreaElement>
      name={name}
      placeholder="Написать..."
      render={(controlProps) => <MessageControl {...props} {...controlProps} />}
      size="small"
      sx={{ [FieldTokens.BaseWidth]: '100%' }}
    >
      {sendBySms && (
        <Typography sx={{ mt: 0.5, color: 'gray' }} variant="caption">
          до 1000 символов, введено: {message.length}, sms - {calcSmsCount(message.length)}
        </Typography>
      )}
      {children}
    </FormField>
  );
};

export interface SendMessageFormProps {
  sx?: SxProps;
  onSent?: () => void;
  SendButton?: (SendButtonProps: ButtonProps) => React.ReactNode;
  tools?: React.ReactNode | React.ReactNode[];
}

export const SendMessageForm = React.forwardRef<HTMLFormElement, SendMessageFormProps>(({ onSent, tools }, ref) => {
  const dialog = useDialog();
  const dialogUserService = useDialogUserService();
  const form = useForm<MessageFormValues>({
    mode: 'onChange',
    resolver,
    defaultValues: {
      dialogId: dialog?.id,
      message: '',
      sendCopyBySms: false,
    },
  });
  const message = form.watch('message');
  const [sendMessage, { isLoading: isSendingMessage }] = coreDialogApi.useSendMessageMutation();
  const handleSendMessage = useCallback(
    async (formValues: MessageFormValues) => {
      const user = dialogUserService.getUser();
      const dto = generateCreateMessageRequest({
        ...formValues,
        dialogId: dialog?.id as number,
        as: user ?? undefined,
      });
      const response = await sendMessage(dto);
      if (response.error) {
        handleApiServerError(response.error, { form, formFieldNameForCommonErrors: 'message' });
        return;
      }

      form.resetField('message', { defaultValue: '' });
      onSent?.();
    },
    [form, sendMessage, onSent, dialog, dialogUserService],
  );
  useEffect(() => {
    if (dialog?.id) {
      form.setValue('dialogId', dialog.id);
    }
  }, [form, dialog?.id]);

  const attachment = !message.length ? (
    <AnnounceHintWrapper>
      {({ setAnnounceMessageVisible }) => <AttachButton onAttach={() => setAnnounceMessageVisible(true)} />}
    </AnnounceHintWrapper>
  ) : null;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const triggerSubmit = useCallback(
    form.handleSubmit(handleSendMessage, () => {
      // Do not show validation errors for message
      form.clearErrors('message');
    }),
    [handleSendMessage, form],
  );

  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    (e) => {
      e.preventDefault();
      triggerSubmit();
      form.setFocus('message');
    },
    [form, triggerSubmit],
  );

  const onKeyDown: KeyboardEventHandler<HTMLTextAreaElement> = useCallback(
    (e) => {
      if (e.key !== 'Enter' || e.ctrlKey || e.shiftKey || e.metaKey) {
        return;
      }
      e.preventDefault();
      triggerSubmit();
    },
    [triggerSubmit],
  );

  return (
    <FormProvider {...form}>
      <form onSubmit={onSubmit} ref={ref}>
        <DialogFooter>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {tools}
            <FormField name="sendCopyBySms" render={(controlProps) => <CheckboxButton {...controlProps} label="sms" size="small" />} />
            {attachment}
          </Box>
          <MessageFormField onKeyDown={onKeyDown} />
          <SendButton disabled={!form.formState.isValid || isSendingMessage} size={48} />
        </DialogFooter>
      </form>
    </FormProvider>
  );
});
